import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Divider, Heading } from "@nn-design-system/react-component-library";
import CustomInput from "../../../../../Components/CustomInputs/CustomInput";
import CountryAutocompleteField from "../../../../../Components/CustomInputs/CountryAutocompleteField";
import GridContainer from "../../../../../Components/ReportFormTemplate/GridContainer";
import WholeLineContainer from "../../../../../Components/ReportFormTemplate/WholeLineContainer";
import { StoreType } from "../../../../../redux/store/store";
import { updateSecondStepLegalRepresentativeFields } from "../../../../../redux/reducers/claimReducer";
import { handleShowErrorMessage } from "../../../../../redux/reducers/claimFieldErrorsReducer";
import IdentificationDataBlock from "../../../../../Components/IdentificationDataBlock/IdentificationDataBlock";
import GetBirthDateFromPersonalId from "../../../../../Utils/GetBirthDateFromPersonalId";

const LegalRepresentativeSection: React.FC = () => {
   const dispatch = useDispatch();
   const { reporterRole, legalRepresentativeModel } = useSelector(
      (state: StoreType) => state.claimState.reportModel
   );
   const claimFieldErrorsMessages = useSelector(
      (state: StoreType) => state.claimFieldErrorsState
   );
   const isDeathClaim = useSelector((state: StoreType) =>
      state.claimState.selectedTypeOfClaims.some((claim) => claim === "death")
   );

   if (isDeathClaim || reporterRole !== 1) {
      return null;
   }

   const model = legalRepresentativeModel as Record<string, string | boolean>;
   const updateField = (field: string, value: string | boolean) => {
      dispatch(updateSecondStepLegalRepresentativeFields({ field, value }));
      if (
         field === "legalRepresentativePersonalId" &&
         typeof value === "string" &&
         !model.legalRepresentativeDateOfBirth
      ) {
         const dateOfBirth = GetBirthDateFromPersonalId(value);
         if (dateOfBirth) {
            dispatch(updateSecondStepLegalRepresentativeFields({
               field: "legalRepresentativeDateOfBirth",
               value: dateOfBirth,
            }));
         }
      }
   };
    const fields = [
       ["legalRepresentativeFirstname", "Jméno"],
       ["legalRepresentativeLastname", "Příjmení"],
       ["legalRepresentativePhoneNumber", "Telefon"],
       ["legalRepresentativeEmail", "E-mail"],
       ["legalRepresentativePersonalId", "Rodné číslo"],
      ["legalRepresentativeDateOfBirth", "Datum narození"],
      ["legalRepresentativePlaceOfBirth", "Místo narození"],
   ];
   const addressFields = [
      ["legalRepresentativeAddress", "Ulice a č.p."],
      ["legalRepresentativePostCode", "PSČ"],
      ["legalRepresentativeCity", "Obec"],
    ];

   return (
      <GridContainer>
         <WholeLineContainer>
            <>
               <Heading variant="Medium" hasMargin={false} mb=".5rem" mt="1rem">
                  Osobní údaje zákonného zástupce
               </Heading>
               <Divider variant="Brand" />
            </>
         </WholeLineContainer>
         {fields.map(([field, label]) => (
            <CustomInput
               key={field}
               labelText={label}
               value={model[field] as string}
               validationText={handleShowErrorMessage(
                   field.charAt(0).toUpperCase() + field.slice(1),
                   claimFieldErrorsMessages
                )}
               type={field.endsWith("DateOfBirth") ? "date" : undefined}
               onChange={(e) => updateField(field, e.target.value)}
            />
         ))}
         <CountryAutocompleteField
            labelText="Státní příslušnost"
            placeholderText="Uveďte státní příslušnost"
            value={model.legalRepresentativeCitizenship as string}
            validationText={handleShowErrorMessage(
               "LegalRepresentativeCitizenship",
               claimFieldErrorsMessages
            )}
            dependencyValue={true}
            fieldNameForScroll="LegalRepresentativeCitizenship"
            onChange={(value) => updateField("legalRepresentativeCitizenship", value)}
         />
         <WholeLineContainer>
            <Checkbox
               labelText="Trvalá adresa se liší od adresy pojištěné osoby"
               variant="Small"
               onChange={() =>
                  updateField(
                     "legalRepresentativeIsAddressDifferentFromInsured",
                     !model.legalRepresentativeIsAddressDifferentFromInsured
                  )
               }
               dataState={
                  model.legalRepresentativeIsAddressDifferentFromInsured
                     ? "Checked"
                     : "NotChecked"
               }
            />
         </WholeLineContainer>
         {model.legalRepresentativeIsAddressDifferentFromInsured &&
            addressFields.map(([field, label]) => (
               <CustomInput
                  key={field}
                  labelText={label}
                  value={model[field] as string}
                  validationText={handleShowErrorMessage(
                     field.charAt(0).toUpperCase() + field.slice(1),
                     claimFieldErrorsMessages
                  )}
                  onChange={(e) => updateField(field, e.target.value)}
               />
            ))}
         {model.legalRepresentativeIsAddressDifferentFromInsured && (
            <CountryAutocompleteField
               labelText="Země"
               placeholderText="Uveďte zemi"
               value={model.legalRepresentativeCountry as string}
               validationText={handleShowErrorMessage(
                  "LegalRepresentativeCountry",
                  claimFieldErrorsMessages
               )}
               dependencyValue={true}
               fieldNameForScroll="LegalRepresentativeCountry"
               onChange={(value) => updateField("legalRepresentativeCountry", value)}
            />
         )}
          <IdentificationDataBlock
             checked={Boolean(model.legalRepresentativePerformAmlIdentification)}
             identityCardType={model.legalRepresentativeIdentityCardType as string}
             identityCardNumber={model.legalRepresentativeIdentityCardNumber as string}
             identityCardIssuedBy={model.legalRepresentativeIdentityCardIssuedBy as string}
             identityCardValidUntil={model.legalRepresentativeIdentityCardValidUntil as string}
             updateField={(field, value) =>
                updateField(
                   field === "checked"
                      ? "legalRepresentativePerformAmlIdentification"
                      : `legalRepresentative${field.charAt(0).toUpperCase()}${field.slice(1)}`,
                   value
                )
             }
             validationKeys={{
                identityCardType: "LegalRepresentativeIdentityCardType",
                identityCardNumber: "LegalRepresentativeIdentityCardNumber",
                identityCardIssuedBy: "LegalRepresentativeIdentityCardIssuedBy",
                identityCardValidUntil: "LegalRepresentativeIdentityCardValidUntil",
             }}
             helperText="Pokud očekávaná výše pojistného plnění přesahuje 25 000 Kč, vyplňte prosím část „Doplnit identifikační údaje“ podle platného průkazu totožnosti zákonného zástupce."
             fieldErrors={claimFieldErrorsMessages}
          />
      </GridContainer>
   );
};

export default LegalRepresentativeSection;
